import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../auth/useAuth'
import { getOrganizationAccess, listProperties } from '../properties/property-service'
import { getErrorMessage } from '../../lib/error-message'
import { listEstimateCategories } from './visual-estimate-service'
import {
  buildDiagnosisRequestDescription,
  getLatestHomeDiagnosis,
  publishRequestFromDiagnosis,
  runHomeDiagnosis,
  type HomeDiagnosis,
} from './home-diagnosis-service'
import { useI18n } from '../i18n/useI18n'
import './amigo-visual-estimate.css'

const money = (amount: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(amount)

export function AmigoHomeDiagnosisPage() {
  const { user } = useAuth()
  const { locale } = useI18n()
  const navigate = useNavigate()
  const [problem, setProblem] = useState('')
  const [propertyId, setPropertyId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [diagnosis, setDiagnosis] = useState<HomeDiagnosis | null>(null)
  const [publishedRequestId, setPublishedRequestId] = useState<string | null>(null)

  const access = useQuery({ queryKey: ['organization-access', user?.id], queryFn: getOrganizationAccess, enabled: Boolean(user) })
  const properties = useQuery({ queryKey: ['properties', access.data?.organizationId], queryFn: () => listProperties(access.data!.organizationId), enabled: Boolean(access.data) })
  const categories = useQuery({ queryKey: ['visual-estimate-categories'], queryFn: listEstimateCategories })

  const selectedProperty = properties.data?.find((item) => item.id === propertyId)
  const selectedCategory = categories.data?.find((item) => item.id === categoryId)
  const savedDiagnosis = useQuery({ queryKey: ['home-diagnosis', propertyId, categoryId], queryFn: () => getLatestHomeDiagnosis(propertyId, categoryId), enabled: Boolean(propertyId && categoryId) })

  const diagnosisMutation = useMutation({
    mutationFn: runHomeDiagnosis,
    onSuccess: (result) => {
      setDiagnosis(result)
      setPublishedRequestId(null)
    },
  })

  const publishMutation = useMutation({
    mutationFn: publishRequestFromDiagnosis,
    onSuccess: (requestId) => setPublishedRequestId(requestId),
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!access.data || !selectedProperty || !selectedCategory) return
    diagnosisMutation.mutate({
      organizationId: access.data.organizationId,
      propertyId: selectedProperty.id,
      categoryId: selectedCategory.id,
      problem,
      categoryName: selectedCategory.name,
      propertySummary: `${selectedProperty.name}, ${selectedProperty.city}, ${selectedProperty.region} ${selectedProperty.postal_code}`,
      locale,
    })
  }

  function publish() {
    if (!diagnosis || !user || !access.data || !selectedProperty || !selectedCategory || diagnosis.emergency) return
    publishMutation.mutate({
      organizationId: access.data.organizationId,
      propertyId: selectedProperty.id,
      categoryId: selectedCategory.id,
      userId: user.id,
      title: `${selectedCategory.name}: ${diagnosis.summary.slice(0, 90)}`,
      description: buildDiagnosisRequestDescription(problem, diagnosis),
      city: selectedProperty.city,
      region: selectedProperty.region,
      postalCode: selectedProperty.postal_code,
      budgetMin: diagnosis.estimated_cost_min,
      budgetMax: diagnosis.estimated_cost_max,
    })
  }

  const displayedDiagnosis = diagnosis ?? savedDiagnosis.data?.diagnosis ?? null

  const error = access.error ?? properties.error ?? categories.error ?? diagnosisMutation.error ?? publishMutation.error

  return <section className="marketplace-page amigo-visual-estimate" aria-labelledby="home-diagnosis-title">
    <p className="eyebrow">AMIGO Home Diagnosis™</p>
    <div className="page-heading">
      <div>
        <h1 id="home-diagnosis-title">Describe what is wrong with the property</h1>
        <p className="lede">AMIGO will organize the symptoms, identify possible causes, flag urgency, estimate a preliminary planning range and prepare a service request.</p>
      </div>
      <Link to="/marketplace" className="text-button">Back to marketplace</Link>
    </div>

    {error && <div className="form-error" role="alert">{getErrorMessage(error, 'Unable to complete the home diagnosis.')}</div>}

    <div className="marketplace-grid">
      <article className="panel">
        <h2>1. Tell AMIGO what you are seeing</h2>
        <form className="marketplace-form" onSubmit={submit}>
          <label>Property
            <select value={propertyId} onChange={(event) => setPropertyId(event.target.value)} required>
              <option value="">Select property</option>
              {properties.data?.map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}
            </select>
          </label>
          <label>Closest service category
            <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} required>
              <option value="">Select category</option>
              {categories.data?.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </label>
          <label className="span-2">What problem did you notice?
            <textarea
              value={problem}
              onChange={(event) => setProblem(event.target.value)}
              minLength={10}
              maxLength={2500}
              required
              placeholder="Example: There is a damp spot and musty smell on the bathroom ceiling. It gets worse after the upstairs shower is used."
            />
          </label>
          <div className="span-2 form-note">Include when it started, where it appears, whether it is getting worse, unusual sounds or smells, and anything that triggers it. Do not enter sensitive personal information.</div>
          <button className="span-2" disabled={diagnosisMutation.isPending || !propertyId || !categoryId || problem.trim().length < 10}>
            {diagnosisMutation.isPending ? 'AMIGO is evaluating the symptoms…' : 'Diagnose the problem'}
          </button>
        </form>
        <div className="measurement-panel" style={{ marginTop: '1rem' }}>
          <strong>For immediate danger</strong>
          <p>For fire, gas odor, sparking, carbon-monoxide symptoms, major flooding near electricity or structural collapse, leave the area when appropriate and contact 911, the utility emergency line or a licensed emergency professional.</p>
        </div>
      </article>

      <article className="panel">
        <h2>2. Preliminary diagnosis</h2>
        {!displayedDiagnosis && <p className="marketplace-empty">Possible causes, risk level, immediate actions, planning cost and the recommended trade will appear here.</p>}
        {displayedDiagnosis && <div className="visual-estimate-result">
          <div className="marketplace-status-row">
            <strong>{displayedDiagnosis.emergency ? 'Immediate safety action required' : 'AMIGO preliminary assessment'}</strong>
            <span className="status-pill">{displayedDiagnosis.risk_level} risk</span>
          </div>
          <h3>{displayedDiagnosis.summary}</h3>
          <p><strong>Recommended professional:</strong> {displayedDiagnosis.recommended_trade}</p>
          <p><strong>AI confidence:</strong> {displayedDiagnosis.confidence}</p>

          {displayedDiagnosis.emergency && <div className="form-error" role="alert">Do not wait for marketplace responses. Follow the immediate safety actions below and contact emergency or utility services when appropriate.</div>}

          <h3>Possible causes</h3>
          <ul>{displayedDiagnosis.likely_causes.map((item) => <li key={item}>{item}</li>)}</ul>

          <h3>What to do now</h3>
          <ul>{displayedDiagnosis.immediate_actions.map((item) => <li key={item}>{item}</li>)}</ul>

          <div className="visual-estimate-breakdown">
            <p><strong>Planning range</strong><br />{money(displayedDiagnosis.estimated_cost_min)} – {money(displayedDiagnosis.estimated_cost_max)}</p>
            <p><strong>Estimated time</strong><br />{displayedDiagnosis.estimated_time}</p>
            <p><strong>Risk level</strong><br />{displayedDiagnosis.risk_level}</p>
          </div>

          <h3>Questions for the provider</h3>
          <ul>{displayedDiagnosis.questions_for_provider.map((item) => <li key={item}>{item}</li>)}</ul>
          <p className="form-note">{displayedDiagnosis.disclaimer}</p>

          {!displayedDiagnosis.emergency && !publishedRequestId && <button type="button" onClick={publish} disabled={publishMutation.isPending}>
            {publishMutation.isPending ? 'Creating service request…' : 'Create and publish service request'}
          </button>}
          {publishedRequestId && <div className="measurement-panel">
            <strong>Service request published</strong>
            <p>PropertyReady has included the reported problem, possible causes, safety steps, provider questions and preliminary budget range.</p>
            <button type="button" onClick={() => void navigate({ to: '/marketplace' })}>View marketplace</button>
          </div>}
        </div>}
      </article>
    </div>
  </section>
}
